import React, { use, useEffect, useState } from 'react';
import useTimelineQuery from '@hooks/orders-sales/use-timeline-query';
import { Product } from '@interfaces/models/orderSaleProduct';
import { SubmitShippingLabelParam, submitShippingLabel } from '@services/my-orders-sales-service';
import { useMutation } from '@tanstack/react-query';
import { ShipmentLabelTypeEnum, ShippingLabelCarrier, ShippingPickupTypeEnum } from '@interfaces/models/timeline';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { ShippingAddress } from '@interfaces/api/responses/user-shipping-address';
import useGetCarriers from '@hooks/orders-sales/use-get-carriers';
import { AxiosError } from 'axios';
import { EmptyResponse } from '@interfaces/api/responses/timeline-action';
import { StepInfoContext } from '@context/step-info.context';
import { TimelineContext } from '@context/timeline.context';
import useGetShippingAddresses from '@hooks/orders-sales/use-get-shipping-address';
import { GenerateShippingLabel } from '@interfaces/api/responses/my-sale';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { useAnalytics } from '@context/analytics/analytics.context';
import popinStyles from '../../popin.module.scss';
import { OkButtonView, PopinView } from '../common';
import styles from './select-carrier-options.module.scss';
import { SubmitErrorModal } from './error-modal';
import CarrierCollapse from './collapse/carrier-collapse';
import LabelTypeOptions from './label-type-options';
import ShippingOptions from './shipping-options';

const LANG_PREFIX = 'MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.';
const CLASSNAME = 'getShippingLabelModal';

interface SelectCarrierAndOptionProps {
  closePopin: () => void;
  product: Product;
  address?: ShippingAddress;
}

const SelectCarrierAndOptions = ({ closePopin, product, address }: SelectCarrierAndOptionProps) => {
  const { t } = useTranslation('orders-sales');

  const { scriptsEventData } = useAnalytics();
  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  const { showToast } = use(TimelineContext);
  const { showAddressForm } = use(StepInfoContext);

  const [selectedCarrier, setSelectedCarrier] = useState<ShippingLabelCarrier>();

  const { selectedAddress } = useGetShippingAddresses(!address);

  const shippingAddress = address || selectedAddress;

  const { refetchTimeline } = useTimelineQuery(product.productId, null, false);

  const {
    mutate,
    isLoading: isSubmitting,
    error: submitError,
    reset,
  } = useMutation<GenerateShippingLabel, AxiosError<EmptyResponse>, SubmitShippingLabelParam>({
    mutationFn: (param: SubmitShippingLabelParam) => submitShippingLabel(param),
  });

  const { data: carriers, isFetching: loadingCarrier } = useGetCarriers(product.productId);

  const handleSelectCarrier = (carrier: ShippingLabelCarrier) => {
    const { shipmentLabelTypes, dropOffTypes } = carrier;

    const hasShipmentData = shipmentLabelTypes.some((s) => s.selected);

    const hasPickUpData = dropOffTypes.some((s) => s.selected);

    // Set default Label Type
    if (!hasShipmentData) {
      const defaultLabelType =
        shipmentLabelTypes.find((o) => o.type === ShipmentLabelTypeEnum.shippingCode) || shipmentLabelTypes[0];

      carrier.shipmentLabelTypes = shipmentLabelTypes.map((p) => ({
        ...p,
        selected: p.type === defaultLabelType.type,
      }));
    }

    // Set default Pick Up
    if (!hasPickUpData) {
      const defaultPickUp = dropOffTypes.find((o) => o.type === ShippingPickupTypeEnum.dropOff) || dropOffTypes[0];

      carrier.dropOffTypes = dropOffTypes.map((d) => ({
        ...d,
        selected: d.type === defaultPickUp.type,
      }));
    }

    setSelectedCarrier(carrier);
  };

  // Handle populate selected carrier
  useEffect(() => {
    if (!carriers || !carriers.length) {
      return;
    }
    if (carriers.length === 1) {
      handleSelectCarrier(carriers[0]);
    } else {
      const carrier = carriers.find((d) => d.selected) || carriers[0];
      handleSelectCarrier(carrier);
    }
  }, [carriers]);

  // Add View tracking action as structured event
  useEffect(() => {
    if (scriptsEventData?.isTcEvents1Loaded && scriptsEventData?.isTcEvents7Loaded) {
      if (carriers && carriers.length) {
        const carrierNames = carriers.map((c) => ({
          carrierName: c.name,
          shippingMethods: c.dropOffTypes.map((s) => s.type).join(', '),
          labelTypes: c.shipmentLabelTypes.map((s) => s.type).join(', '),
        }));
        sendAnalyticEvent('select_carrier_and_options', { label: JSON.stringify(carrierNames) });
      }
    }
  }, [carriers, scriptsEventData?.isTcEvents1Loaded, scriptsEventData?.isTcEvents7Loaded]);

  const onClose = () => {
    closePopin();
  };

  const generateLabel = () => {
    const labelType = selectedCarrier?.shipmentLabelTypes.find((l) => l.selected);
    const pickupType = selectedCarrier?.dropOffTypes.find((d) => d.selected);

    if (!labelType || !pickupType) {
      return;
    }

    const trackingPayload = {
      carrier_name: selectedCarrier.name,
      shipping_methods: pickupType.type || 'N/A',
      label_types: labelType.type || 'N/A',
    };

    sendAnalyticEvent('timeline_confirm_select_carrier_and_options', { label: JSON.stringify(trackingPayload) });

    mutate(
      {
        productId: product.productId,
        carrierName: selectedCarrier.name,
        dropOffType: pickupType.type,
        shipmentLabelType: labelType.type,
      },
      {
        onSuccess: (res) => {
          if (res.labelType === ShipmentLabelTypeEnum.printableLabel && res.shippingLabelUrl) {
            window.open(res.shippingLabelUrl, '_blank');
          }
          refetchTimeline();
          onClose();
        },
      },
    );
  };

  if (loadingCarrier) {
    return (
      <PopinView
        closePopin={onClose}
        title={t(`${LANG_PREFIX}TITLE`)}
      >
        <div className={popinStyles.ordersSales__popin__loading} />
      </PopinView>
    );
  }
  if (!carriers) {
    return (
      <PopinView
        closePopin={onClose}
        title={t(`${LANG_PREFIX}TITLE`)}
      >
        <div className={styles[`${CLASSNAME}__form`]}>
          <p>{t('MY_ACCOUNT.ORDERS_SALES.ERROR.API')}</p>
        </div>
      </PopinView>
    );
  }

  const isAllowSubmit =
    selectedCarrier &&
    selectedCarrier.shipmentLabelTypes.some((l) => l.selected) &&
    selectedCarrier.dropOffTypes.some((d) => d.selected);

  return (
    <PopinView
      closePopin={onClose}
      title={t(`${LANG_PREFIX}TITLE`)}
    >
      <div className={styles[`${CLASSNAME}__form`]}>
        {carriers.map((c) => {
          const isSelected = c.name === selectedCarrier?.name;
          const hasOneOption = carriers.length === 1;
          return (
            <CarrierCollapse
              key={c.name}
              carrier={c}
              isSelected={isSelected}
              hasOneOption={hasOneOption}
              handleSelectCarrier={(carrier) => {
                sendAnalyticEvent('timeline_select_carrier_and_options_carrier_selection', { label: carrier.name });
                handleSelectCarrier(carrier);
              }}
            >
              <LabelTypeOptions
                handleSelectCarrier={handleSelectCarrier}
                carrier={c}
              />

              <div className={styles[`${CLASSNAME}_divider`]} />

              <ShippingOptions
                handleSelectCarrier={handleSelectCarrier}
                carrier={c}
                address={shippingAddress}
                productId={product.productId}
              />
            </CarrierCollapse>
          );
        })}

        <div className={styles[`${CLASSNAME}__form__buttonContainer`]}>
          <div
            className={clsx(
              styles[`${CLASSNAME}__form__button`],
              isAllowSubmit ? styles[`${CLASSNAME}__form__button--allowSubmit`] : '',
            )}
          >
            <OkButtonView
              isSubmitting={isSubmitting}
              disabled={!isAllowSubmit}
              onClick={generateLabel}
            >
              {t('MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.GENERATE')}
            </OkButtonView>
          </div>
        </div>
      </div>

      {!!submitError && (
        <SubmitErrorModal
          productId={product.productId}
          onClose={reset}
          errorMessage={submitError.response?.data?.errors?.detail?.toString()}
          errorObjects={submitError.response?.data?.errors?.objects}
          onGenerateReviewAddress={() => {
            reset();
            showAddressForm(true);
            onClose();
          }}
          onTicketSupportSent={() => {
            showToast({
              title: t('MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.GENERATION_FAILED_DESCRIPTION_ZENDESK'),
              type: 'success',
            });
            onClose();
          }}
        />
      )}
    </PopinView>
  );
};

export default SelectCarrierAndOptions;
